/**
 * The category strip above the item grid.
 *
 * All tabs are the same fixed width and height so the strip looks like a
 * proper button group rather than a ragged label cloud.
 *
 * Below `lg` it scrolls sideways instead of wrapping. A shop with a dozen
 * categories would otherwise wrap to four rows on a phone and leave nothing
 * below them for the items themselves.
 */

import { useDispatch, useSelector } from 'react-redux';
import { selectCategories, selectMenu, setActiveCategory } from '../../store/menuSlice';

const CategoryTabs = () => {
    const dispatch = useDispatch();
    const categories = useSelector(selectCategories);
    const { activeCategoryId, items } = useSelector(selectMenu);

    return (
        <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto scrollbar-none pb-0
                        sm:gap-2 lg:flex-wrap lg:overflow-visible">

            {/* All Items — virtual first tab */}
            <button
                type="button"
                onClick={() => dispatch(setActiveCategory(null))}
                className={`shrink-0 flex flex-col items-center justify-center
                            w-[88px] h-[44px] sm:w-[105px] sm:h-[42px] rounded-xl text-xs font-semibold
                            transition-all duration-150
                            ${activeCategoryId === null
                        ? 'bg-brand-700 text-white shadow-md ring-2 ring-brand-300/50 ring-offset-1'
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-brand-400 hover:text-brand-700 hover:bg-brand-50 shadow-sm'}`}
            >
                <span className="leading-tight">All Items</span>
                <span className={`mt-0.5 tabular text-[11px] font-bold
                                  ${activeCategoryId === null ? 'text-white/80' : 'text-slate-400'}`}>
                    {items.length}
                </span>
            </button>

            {categories.map((category) => {
                const isActive = activeCategoryId === category.categoryId;

                return (
                    <button
                        key={category.categoryId}
                        type="button"
                        onClick={() => dispatch(setActiveCategory(category.categoryId))}
                        className={`shrink-0 flex flex-col items-center justify-center
                                    w-[88px] h-[44px] sm:w-[105px] sm:h-[42px] rounded-xl text-xs font-semibold
                                    transition-all duration-150
                                    ${isActive
                                ? 'text-white shadow-md ring-2 ring-offset-1'
                                : 'bg-white text-slate-600 border border-slate-200 hover:shadow-sm shadow-sm'}`}
                        style={
                            isActive && category.colour
                                ? { backgroundColor: category.colour, borderColor: category.colour, '--tw-ring-color': category.colour + '66' }
                                : isActive
                                    ? { backgroundColor: '#152fe1', borderColor: '#152fe1' }
                                    : category.colour
                                        ? { borderTopWidth: '3px', borderTopColor: category.colour }
                                        : undefined
                        }
                    >
                        <span className="truncate max-w-[70px] sm:max-w-[78px] text-center leading-tight px-1">
                            {category.name}
                        </span>
                        {category.itemCount > 0 && (
                            <span className={`mt-0.5 tabular text-[11px] font-bold
                                             ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                                {category.itemCount}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

export default CategoryTabs;
